import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { Switch } from '../Switch/Switch';
import { Label } from '../Label/Label';
import { Select } from '../Select/Select';
import { Button } from '../Button/Button';
import { Alert } from '../Alert/Alert';
import type {
  EffectiveSchedulingSettings,
  TeamSchedulingSettingsInput,
  OrganizationSchedulingSettingsInput,
  TeamSchedulingSettings,
} from '../../api/types/scheduling-settings.types';
import { getCountryOptions } from '../../api/types/scheduling-settings.types';
import type { SchedulingSettingsFormValues } from './SchedulingSettingsForm.schema';
import { TimeInputGroup } from './TimeInputGroup';
import { SectionHeader } from './SectionHeader';
import { validateFormValues, confirmDeletion } from './formHandlers';
import classes from './SchedulingSettingsForm.module.css';

type SchedulingSettingsInput = TeamSchedulingSettingsInput | OrganizationSchedulingSettingsInput;

interface SchedulingSettingsFormProps {
  effectiveSettings: EffectiveSchedulingSettings;
  hasCustomSettings?: boolean;
  teamSettings?: TeamSchedulingSettings | null;
  saveMutation: UseMutationResult<void, Error, SchedulingSettingsInput>;
  deleteMutation?: UseMutationResult<void, Error, void>;
  inputType?: 'textfield' | 'select';
  workHoursDescription?: string;
  weekendHolidaysDescription?: string;
}

export const SchedulingSettingsForm = ({
  effectiveSettings,
  hasCustomSettings = false,
  saveMutation,
  deleteMutation,
  inputType = 'textfield',
  workHoursDescription,
  weekendHolidaysDescription,
}: SchedulingSettingsFormProps) => {
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState<SchedulingSettingsFormValues>({
    workdayStartHour: effectiveSettings.workdayStartHour,
    workdayStartMinute: effectiveSettings.workdayStartMinute,
    workdayEndHour: effectiveSettings.workdayEndHour,
    workdayEndMinute: effectiveSettings.workdayEndMinute,
    skipWeekends: effectiveSettings.skipWeekends,
    skipHolidays: effectiveSettings.skipHolidays,
    holidayCountryCode: effectiveSettings.holidayCountryCode,
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    setFormValues({
      workdayStartHour: effectiveSettings.workdayStartHour,
      workdayStartMinute: effectiveSettings.workdayStartMinute,
      workdayEndHour: effectiveSettings.workdayEndHour,
      workdayEndMinute: effectiveSettings.workdayEndMinute,
      skipWeekends: effectiveSettings.skipWeekends,
      skipHolidays: effectiveSettings.skipHolidays,
      holidayCountryCode: effectiveSettings.holidayCountryCode,
    });
  }, [effectiveSettings]);

  const handleSave = () => {
    const error = validateFormValues(formValues, t);
    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError(null);
    saveMutation.mutate(formValues);
  };

  const handleDelete = () => {
    if (!deleteMutation) return;

    if (!confirmDeletion()) {
      return;
    }

    setValidationError(null);
    deleteMutation.mutate();
  };

  const isPending = saveMutation.isPending || (deleteMutation?.isPending ?? false);
  const error = saveMutation.error || deleteMutation?.error;

  return (
    <div className={classes.form}>
      {/* Work hours section */}
      <div className={classes.section}>
        <SectionHeader
          title={t('scheduling.form.sections.workHours')}
          description={workHoursDescription}
        />

        <div className={classes.timeInputs}>
          <TimeInputGroup
            label={t('scheduling.form.labels.start')}
            hour={formValues.workdayStartHour}
            minute={formValues.workdayStartMinute}
            onHourChange={(hour) =>
              setFormValues({ ...formValues, workdayStartHour: hour })
            }
            onMinuteChange={(minute) =>
              setFormValues({ ...formValues, workdayStartMinute: minute })
            }
            inputType={inputType}
            disabled={isPending}
          />

          <span className={classes.timeConnector}>{t('scheduling.form.labels.to')}</span>

          <TimeInputGroup
            label={t('scheduling.form.labels.end')}
            hour={formValues.workdayEndHour}
            minute={formValues.workdayEndMinute}
            onHourChange={(hour) =>
              setFormValues({ ...formValues, workdayEndHour: hour })
            }
            onMinuteChange={(minute) =>
              setFormValues({ ...formValues, workdayEndMinute: minute })
            }
            inputType={inputType}
            disabled={isPending}
          />
        </div>
      </div>

      {/* Weekend and holidays section */}
      <div className={classes.section}>
        <SectionHeader
          title={t('scheduling.form.sections.weekendHolidays')}
          description={weekendHolidaysDescription}
        />

        <div className={classes.switchGroup}>
          <div className={classes.switchItem}>
            <Label
              label={t('scheduling.form.labels.skipWeekends')}
              htmlFor="skipWeekends"
              size="md"
              spacing={false}
            />
            <Switch
              key={`skipWeekends-${formValues.skipWeekends}`}
              id="skipWeekends"
              defaultState={formValues.skipWeekends}
              setParentValue={(value) =>
                setFormValues({ ...formValues, skipWeekends: value })
              }
            />
          </div>

          <div className={classes.switchItem}>
            <Label
              label={t('scheduling.form.labels.skipHolidays')}
              htmlFor="skipHolidays"
              size="md"
              spacing={false}
            />
            <Switch
              key={`skipHolidays-${formValues.skipHolidays}`}
              id="skipHolidays"
              defaultState={formValues.skipHolidays}
              setParentValue={(value) =>
                setFormValues({ ...formValues, skipHolidays: value })
              }
            />
          </div>
        </div>

        {formValues.skipHolidays && (
          <div className={classes.selectWrapper}>
            <Label
              label={t('scheduling.form.labels.countryLabel')}
              htmlFor="holidayCountryCode"
              size="md"
              spacing={true}
            />
            <Select
              id="holidayCountryCode"
              options={getCountryOptions().map((country) => ({
                label: country.name,
                value: country.code,
              }))}
              value={formValues.holidayCountryCode}
              onChange={(value) =>
                setFormValues({ ...formValues, holidayCountryCode: value })
              }
              placeholder={t('scheduling.form.labels.selectCountry')}
              required
            />
          </div>
        )}
      </div>

      {/* Error messages */}
      {validationError && (
        <Alert variant="danger" size="md" title={t('scheduling.form.messages.validationError')}>
          {validationError}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" size="md" title={t('scheduling.form.messages.error')}>
          {error.message || t('scheduling.form.messages.saveFailed')}
        </Alert>
      )}

      {/* Actions */}
      <div className={classes.actions}>
        <Button variant="success" onClick={handleSave} disabled={isPending}>
          {saveMutation.isPending ? t('scheduling.form.buttons.saving') : t('scheduling.form.buttons.save')}
        </Button>

        {hasCustomSettings && deleteMutation && (
          <Button
            onClick={handleDelete}
            variant="secondary"
            disabled={isPending}
          >
            {deleteMutation.isPending
              ? t('scheduling.form.buttons.deleting')
              : t('scheduling.form.buttons.delete')}
          </Button>
        )}
      </div>
    </div>
  );
};
